/**
 * Typed API client for the ASHI backend.
 *
 * In dev, Vite proxies `/api/*` to http://localhost:8000 (see vite.config.ts).
 * In prod, VITE_API_BASE_URL points at the API origin.
 *
 * Auth: HttpOnly cookies set by /api/auth/exchange. Every fetch uses
 * `credentials: "include"` so the browser sends the cookie cross-origin.
 * No bearer token is baked into the bundle.
 */
import type { ChatStreamEvent } from "./events";
import { parseSSEEvent } from "./events";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "";

/** JSON-body headers. Auth flows via cookies, not headers. */
function jsonHeaders(): HeadersInit {
  return {
    "Content-Type": "application/json",
  };
}

// ---- /api/config ----

export interface ServerConfig {
  client_id: string;
  client_name: string;
  tableau_server: string;
  tableau_site: string;
  dashboard_names: string[];
}

export async function fetchConfig(): Promise<ServerConfig> {
  // /api/config is currently public; cookies are sent anyway (harmless).
  const response = await fetch(`${API_BASE}/api/config`, {
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(`Failed to load config: ${response.status}`);
  }
  return response.json();
}

// ---- /api/chat ----

export interface ChatStreamHandlers {
  onEvent: (event: ChatStreamEvent) => void;
  onError: (error: Error) => void;
  onDone: () => void;
  signal?: AbortSignal;
}

/**
 * POST to /api/chat and stream Server-Sent Events back.
 *
 * Caller passes callbacks for each event; we parse the SSE wire format
 * byte-by-byte to handle whatever chunking the browser gives us.
 *
 * Returns nothing — observe state via the callbacks.
 */
export async function streamChat(
  conversationId: string,
  message: string,
  handlers: ChatStreamHandlers,
): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/api/chat`, {
      method: "POST",
      credentials: "include",
      headers: jsonHeaders(),
      body: JSON.stringify({
        conversation_id: conversationId,
        message,
      }),
      signal: handlers.signal,
    });
  } catch (e) {
    handlers.onError(e instanceof Error ? e : new Error(String(e)));
    handlers.onDone();
    return;
  }

  if (!response.ok) {
    let detail = `HTTP ${response.status}`;
    try {
      const body = await response.json();
      if (body?.detail) detail = `${detail}: ${body.detail}`;
    } catch {
      // Empty body or non-JSON; keep generic message
    }
    handlers.onError(new Error(detail));
    handlers.onDone();
    return;
  }

  if (!response.body) {
    handlers.onError(new Error("Response has no body"));
    handlers.onDone();
    return;
  }

  // ---- SSE parser ----
  // We accumulate bytes in a TextDecoder, then split into events on blank
  // lines. Each event has `event: NAME` and `data: JSON` lines.

  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";
  let currentEvent: string | null = null;
  let currentData: string[] = [];

  const dispatchIfComplete = () => {
    if (currentEvent && currentData.length > 0) {
      const dataStr = currentData.join("\n");
      const parsed = parseSSEEvent(currentEvent, dataStr);
      if (parsed) handlers.onEvent(parsed);
    }
    currentEvent = null;
    currentData = [];
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Pull complete lines from the buffer.
      // SSE uses \n or \r\n line endings.
      let newlineIdx: number;
      while ((newlineIdx = buffer.indexOf("\n")) !== -1) {
        let line = buffer.slice(0, newlineIdx);
        buffer = buffer.slice(newlineIdx + 1);
        if (line.endsWith("\r")) line = line.slice(0, -1);

        if (line === "") {
          // Blank line — event terminator.
          dispatchIfComplete();
          continue;
        }
        if (line.startsWith(":")) {
          // SSE comment (keepalive). Ignore.
          continue;
        }
        if (line.startsWith("event:")) {
          currentEvent = line.slice("event:".length).trim();
          continue;
        }
        if (line.startsWith("data:")) {
          currentData.push(line.slice("data:".length).replace(/^ /, ""));
          continue;
        }
        // Unknown line type — ignore.
      }
    }

    // Flush trailing event if stream ended without a final blank.
    dispatchIfComplete();
  } catch (e) {
    if ((e as Error).name === "AbortError") {
      // Caller cancelled; not an error.
    } else {
      handlers.onError(e instanceof Error ? e : new Error(String(e)));
    }
  } finally {
    handlers.onDone();
  }
}

// ---- /api/conversations/:id (POST) ----

export async function resetConversation(conversationId: string): Promise<void> {
  await fetch(`${API_BASE}/api/conversations/${encodeURIComponent(conversationId)}`, {
    method: "POST",
    credentials: "include",
    headers: jsonHeaders(),
  });
}